/**
 * Mobile Filter Modal
 * Fullscreen modal for filter options on mobile devices
 * Touch-friendly with large hit targets
 */
import { useState, useEffect } from 'react'
import { HiOutlineX, HiOutlineCheck, HiOutlineRefresh } from 'react-icons/hi'
import clsx from 'clsx'

function MobileFilterModal({
  isOpen,
  onClose,
  // Filter values
  location = '',
  timeRange = '',
  sentiment = '',
  priceSegment = '',
  // Filter options
  locations = [],
  timeRangeOptions = [],
  sentimentOptions = [],
  priceSegmentOptions = [],
  // Callbacks
  onApply,
  onReset,
}) {
  // Local state for filter selections
  const [localFilters, setLocalFilters] = useState({
    location,
    timeRange,
    sentiment,
    priceSegment,
  })

  // Sync when props change
  useEffect(() => {
    if (isOpen) {
      setLocalFilters({ location, timeRange, sentiment, priceSegment })
    }
  }, [isOpen, location, timeRange, sentiment, priceSegment])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const handleApply = () => {
    if (onApply) {
      onApply(localFilters)
    }
    onClose()
  }

  const handleReset = () => {
    const resetFilters = {
      location: '',
      timeRange: '',
      sentiment: '',
      priceSegment: '',
    }
    setLocalFilters(resetFilters)
    if (onReset) {
      onReset()
    }
  }

  const updateFilter = (key, value) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }))
  }

  // Count active filters
  const activeCount = Object.values(localFilters).filter(v => v !== '').length

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 md:hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Modal Content - Slide up */}
      <div className="absolute inset-x-0 bottom-0 top-12 bg-white rounded-t-2xl flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-secondary-100">
          <button
            onClick={onClose}
            className="p-2 -ml-2 text-secondary-500 hover:text-secondary-700"
          >
            <HiOutlineX className="w-6 h-6" />
          </button>

          <h2 className="font-semibold text-lg text-secondary-900">
            Bộ lọc nâng cao
            {activeCount > 0 && (
              <span className="ml-2 text-sm font-normal text-primary-600">
                ({activeCount})
              </span>
            )}
          </h2>

          <button
            onClick={handleReset}
            className="p-2 -mr-2 text-secondary-500 hover:text-secondary-700"
          >
            <HiOutlineRefresh className="w-5 h-5" />
          </button>
        </div>

        {/* Filter Sections - Scrollable */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {/* Location Filter */}
          <FilterSection
            title="Khu vực"
            value={localFilters.location}
            options={locations}
            onChange={(val) => updateFilter('location', val)}
          />

          {/* Time Range Filter */}
          <FilterSection
            title="Thời gian"
            value={localFilters.timeRange}
            options={timeRangeOptions}
            onChange={(val) => updateFilter('timeRange', val)}
          />

          {/* Sentiment Filter */}
          <FilterSection
            title="Xu hướng tin"
            value={localFilters.sentiment}
            options={sentimentOptions}
            onChange={(val) => updateFilter('sentiment', val)}
          />

          {/* Price Segment Filter */}
          <FilterSection
            title="Phân khúc giá"
            value={localFilters.priceSegment}
            options={priceSegmentOptions}
            onChange={(val) => updateFilter('priceSegment', val)}
          />
        </div>

        {/* Footer - Fixed */}
        <div className="flex-shrink-0 p-4 border-t border-secondary-100 bg-white pb-safe">
          <button
            onClick={handleApply}
            className="w-full btn-primary py-3.5 text-base font-semibold flex items-center justify-center gap-2"
          >
            <HiOutlineCheck className="w-5 h-5" />
            Áp dụng bộ lọc
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Filter Section Component
 * Radio-style selection with large touch targets
 */
function FilterSection({ title, value, options, onChange }) {
  return (
    <div className="border-b border-secondary-100">
      <h3 className="px-4 py-3 font-medium text-secondary-700 bg-secondary-50 text-sm">
        {title}
      </h3>
      <div className="px-4 py-2">
        {options.map((opt) => {
          const optValue = typeof opt === 'object' ? (opt.value ?? opt.id ?? '') : opt
          const optLabel = typeof opt === 'object' ? (opt.label ?? opt.name ?? opt) : opt
          const isSelected = value === optValue

          return (
            <button
              key={optValue}
              onClick={() => onChange(optValue)}
              className={clsx(
                'w-full flex items-center justify-between py-3.5 px-3 rounded-xl mb-1 transition-colors',
                isSelected
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-secondary-700 active:bg-secondary-50'
              )}
            >
              <span className="text-base">{optLabel}</span>
              {isSelected && (
                <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center">
                  <HiOutlineCheck className="w-4 h-4 text-white" />
                </div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default MobileFilterModal
